import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import {
  type CalculateMonthlyContributionParams,
  calculateMonthlyContribution,
  calculateMonthlyContributionWithFee,
  calculateMonthlyContributionWithoutFee,
} from "Charts/Utils/CommonChartUtils";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { BlCheckboxInput } from "Forms/Components/BlCheckboxInput";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { BlRadioButtons } from "Forms/Components/BlRadioButtons";
import { DatePicker } from "Forms/Components/DatePicker";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { FormSwitch } from "Forms/Components/FormSwitch";
import { type HousingOtherGoalFormType, getHousingOtherFormSchema, minDate } from "Forms/Schemas/Goals/housingOtherFormSchema";
import {
  getGetAnalysisGoalsQueryKey,
  getGetHousingOtherGoalQueryKey,
  useCreateHousingOtherGoal,
  useUpdateHousingOtherGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  GoalOverviewSubType,
  type HousingOtherGoalDto,
  HousingOtherGoalDtoPriorityLevel,
} from "Infrastructure/ApiZfp/Schema";
import { DEFAULT_INFLATION_RATE, INTEREST_RATE_AGGRESIVE, INTEREST_RATE_CONSERVATIVE } from "Infrastructure/Constants/financialConstants";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { differenceInMonths, format, isBefore, startOfDay } from "date-fns";
import { forwardRef, useContext, useEffect, useImperativeHandle } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ModelingAccordion } from "./ModelingAccordion";
import { SavingsChart } from "./SavingsChart";

type Props = {
  readonly onClose: () => void;
  readonly onSubTypeChange: (goalSubType: GoalOverviewSubType) => void;
  readonly editGoal?: HousingOtherGoalDto;
  readonly goalId?: string;
};

export const HousingOtherGoalForm = forwardRef<{}, Props>(({ onSubTypeChange, onClose, editGoal, goalId }, ref) => {
  const { t } = useResource();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;
  const methods = useForm<HousingOtherGoalFormType>({
    resolver: yupResolver(getHousingOtherFormSchema(t)),
    mode: "onBlur",
    defaultValues: editGoal
      ? {
          includeInflation: editGoal.modeling.inflation?.enabled,
          includeFee: editGoal.modeling.fee?.enabled,
          inflationRate: editGoal.modeling.inflation?.rate ?? 0,
          feeRate: editGoal.modeling.fee?.rate ?? 0,
          goalSubType: GoalOverviewSubType.housing_other,
          name: editGoal.name,
          targetDate: new Date(editGoal.data.targetDate as string),
          targetAmount: editGoal.data.targetAmount?.value.toString() as any,
          monthlyContribution: editGoal.modeling.investmentAmount?.value.toString() as any,
          monthlyContributionWithFee: editGoal.modeling.investmentAmount?.value.toString() as any,
          interestRate: editGoal.modeling.appreciationRate?.toString() as any,
          note: editGoal.note || "",
          priority: editGoal.priorityLevel === HousingOtherGoalDtoPriorityLevel.high,
          referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
        }
      : {
          includeInflation: true,
          includeFee: true,
          inflationRate: DEFAULT_INFLATION_RATE,
          feeRate: DEFAULT_INFLATION_RATE,
          goalSubType: GoalOverviewSubType.housing_other,
          name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingOther),
          referenceStartDate: new Date(),
        },
  });

  const {
    control,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
    handleSubmit,
  } = methods;

  const targetDate = watch("targetDate");
  const targetAmount = watch("targetAmount");
  const interestRate = watch("interestRate");
  const includeInflation = watch("includeInflation");
  const includeFee = watch("includeFee");
  const feeRate = watch("feeRate");
  const inflationRate = watch("inflationRate");
  const monthlyContribution = watch("monthlyContribution");
  const referenceStartDate = watch("referenceStartDate");

  const investmentLength = differenceInMonths(targetDate, new Date());
  const isInvalidTargetDate = isBefore(startOfDay(targetDate), startOfDay(minDate));
  const shouldRenderChart =
    targetDate && !isInvalidTargetDate && interestRate >= 0 && inflationRate >= 0 && monthlyContribution && targetAmount;

  useEffect(() => {
    if (editGoal) {
      return;
    }

    if (investmentLength && investmentLength > 120) {
      setValue("interestRate", INTEREST_RATE_AGGRESIVE.toString() as any);
    } else {
      setValue("interestRate", INTEREST_RATE_CONSERVATIVE.toString() as any);
    }
  }, [setValue, investmentLength, editGoal]);

  useEffect(() => {
    const monthlyContribution = calculateMonthlyContribution({
      targetAmount,
      durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
      interestRate,
      inflationRate: includeInflation ? Number(inflationRate) : 0,
    });
    const newContributionWithFee = calculateMonthlyContributionWithFee(monthlyContribution, feeRate);
    setValue("monthlyContributionWithFee", newContributionWithFee.toString() as any);
  }, [feeRate, inflationRate, interestRate, targetAmount, targetDate, includeInflation, setValue]);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(editGoal ? onEditGoal : onCreateGoal)();
    },
  }));

  useEffect(() => {
    if (!targetDate || isInvalidTargetDate) return;
    clearErrors("targetDate");
    onChangeTargetDate();
  }, [targetDate, isInvalidTargetDate, clearErrors]);

  const onChangeTargetDate = () => {
    onChange({
      durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
      targetAmount,
      inflationRate,
      interestRate,
    });
  };

  const queryClient = useQueryClient();

  const { mutate: createHousingOtherGoal } = useCreateHousingOtherGoal();

  const { mutate: updateHousingOtherGoal } = useUpdateHousingOtherGoal();

  const onChange = ({ inflationRate, interestRate, targetAmount, durationInMonths }: CalculateMonthlyContributionParams) => {
    if (interestRate && targetAmount && durationInMonths) {
      const monthlyContribution = calculateMonthlyContribution({
        targetAmount,
        durationInMonths,
        interestRate,
        inflationRate: includeInflation ? Number(inflationRate) : 0,
      });
      setValue("monthlyContribution", monthlyContribution.toString() as any);
    }
  };

  const onCreateGoal = (data: HousingOtherGoalFormType) => {
    createHousingOtherGoal(
      {
        analysisId,
        versionId,
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? HousingOtherGoalDtoPriorityLevel.high : HousingOtherGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.targetDate, "yyyy-MM-dd"),
            targetAmount: {
              value: data.targetAmount,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
          modeling: {
            investmentAmount: {
              value: data.monthlyContributionWithFee,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            inflation: {
              enabled: !!data.includeInflation,
              rate: data.inflationRate,
            },
            fee: {
              enabled: !!data.includeFee,
              rate: data.feeRate,
            },
            appreciationRate: data.interestRate,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onEditGoal = (data: HousingOtherGoalFormType) => {
    updateHousingOtherGoal(
      {
        analysisId,
        versionId,
        goalId: editGoal?.goalId ?? "",
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? HousingOtherGoalDtoPriorityLevel.high : HousingOtherGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.targetDate, "yyyy-MM-dd"),
            targetAmount: {
              value: data.targetAmount,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
          modeling: {
            investmentAmount: {
              value: data.monthlyContributionWithFee,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            inflation: {
              enabled: !!data.includeInflation,
              rate: data.inflationRate,
            },
            fee: {
              enabled: !!data.includeFee,
              rate: data.feeRate,
            },
            appreciationRate: data.interestRate,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({
            queryKey: getGetHousingOtherGoalQueryKey(analysisId, versionId, goalId ?? editGoal?.goalId ?? undefined),
          });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
              {t(Resources.AnalysisDetail.Goals.GoalType.Housing)}
            </Typography>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <BlCheckboxInput
                  control={control}
                  disabled={readOnly}
                  name="priority"
                  text={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                  errors={errors}
                />
              </Grid>
              <Grid size={12}>
                <BlFormInput
                  disabled={readOnly}
                  control={control}
                  name="name"
                  label={t(Resources.AnalysisDetail.Goals.GoalName)}
                  errors={errors}
                  labelEndAdornment={<StyledLabelAdornment />}
                />
              </Grid>
              <Grid size={12}>
                <BlRadioButtons
                  control={control}
                  name="goalSubType"
                  label={t(Resources.AnalysisDetail.Goals.WhatAreYouPlanning)}
                  onChange={(goalSubType) => onSubTypeChange(goalSubType as GoalOverviewSubType)}
                  codeList={[
                    {
                      code: GoalOverviewSubType.housing_property,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingProperty),
                    },
                    {
                      code: GoalOverviewSubType.housing_mortgage,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingMortgage),
                    },
                    {
                      code: GoalOverviewSubType.housing_other,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingOther),
                    },
                  ]}
                  errors={errors}
                  disabled={!!editGoal || readOnly}
                />
              </Grid>
              <Grid size={6}>
                <DatePicker
                  control={control}
                  disabled={readOnly}
                  name="targetDate"
                  label={t(Resources.AnalysisDetail.Goals.PurchaseDate)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  minDate={minDate}
                  errors={errors}
                />
              </Grid>
              <Grid size={6}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="targetAmount"
                  label={t(Resources.AnalysisDetail.Goals.DrawAmount)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  onChange={(targetAmount) =>
                    onChange({
                      durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                      targetAmount: Number(targetAmount),
                      inflationRate,
                      interestRate,
                    })
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      min: 0,
                      scale: 0,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Box>
          {targetDate && targetAmount && (
            <Box>
              <ModelingAccordion>
                <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                  <Grid size={6}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="monthlyContributionWithFee"
                      label={t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 2,
                          radix: ",",
                        },
                      ]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          {t(Resources.Common.CZKSuffix)}
                        </Typography>
                      }
                      onChange={(newContributionWithFee) => {
                        setValue("monthlyContribution", calculateMonthlyContributionWithoutFee(Number(newContributionWithFee), feeRate));
                      }}
                    />
                  </Grid>
                  <Grid size={6}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="interestRate"
                      label={t(Resources.AnalysisDetail.Goals.InterestRate)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      onChange={(newInterestRate) => {
                        if (
                          (includeInflation && Number(newInterestRate) === Number(inflationRate)) ||
                          (!includeInflation && Number(newInterestRate) <= 0)
                        ) {
                          setValue("interestRate", interestRate);
                          return;
                        }
                        onChange({
                          durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                          targetAmount,
                          inflationRate,
                          interestRate: Number(newInterestRate),
                        });
                      }}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 1,
                          radix: ",",
                          min: 0,
                          max: 20,
                        },
                      ]}
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      disabled={Number(interestRate) === Number(inflationRate) || readOnly}
                      control={control}
                      name="includeInflation"
                      label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                      onChange={(checked) => {
                        const newInflationRate = checked ? DEFAULT_INFLATION_RATE : 0;

                        onChange({
                          durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                          targetAmount,
                          inflationRate: Number(newInflationRate),
                          interestRate,
                        });
                      }}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={!includeInflation || readOnly}
                      control={control}
                      name="inflationRate"
                      errors={errors}
                      onChange={(newInflationRate) => {
                        if (Number(newInflationRate) === Number(interestRate)) {
                          setValue("inflationRate", inflationRate);
                          return;
                        }
                        onChange({
                          durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                          targetAmount,
                          inflationRate: Number(newInflationRate),
                          interestRate,
                        });
                      }}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          radix: ",",
                          mapToRadix: ["."],
                          min: 0,
                          max: 20,
                          scale: 1,
                        },
                      ]}
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      disabled={readOnly}
                      name="includeFee"
                      label={t(Resources.AnalysisDetail.Goals.IncludeEntryFee)}
                      onChange={(checked) => {
                        const newFeeRate = checked ? DEFAULT_INFLATION_RATE : 0;

                        setValue("feeRate", newFeeRate);
                      }}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={!includeFee || readOnly}
                      control={control}
                      name="feeRate"
                      errors={errors}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 1,
                          radix: ",",
                          min: 0,
                          max: 20,
                        },
                      ]}
                    />
                  </Grid>
                </Grid>
              </ModelingAccordion>
            </Box>
          )}
          <Box>
            <Grid container spacing={Spacing.XL}>
              {shouldRenderChart && (
                <Grid size={12}>
                  <SavingsChart
                    referenceStartDate={referenceStartDate}
                    targetDate={targetDate}
                    targetAmount={targetAmount}
                    interestRate={interestRate ? Number(interestRate) : 0}
                    monthlyContributionAmount={Number(monthlyContribution)}
                    inflationRate={inflationRate}
                  />
                </Grid>
              )}
              <Grid size={12}>
                <BlFormInput
                  disabled={readOnly}
                  control={control}
                  label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                  name="note"
                  errors={errors}
                  multiline
                  minRows={6}
                  maxRows={8}
                  characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
